import React, { useEffect, useRef, useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import { format } from 'date-fns';

const GET_GOOGLE_MAPS_API_KEY_FOR_MAP = gql`
  query GetGoogleMapsApiKeyForMap {
    getGoogleMapsApiKeyForMap
  }
`;

/**
 * GPSTrackingMapView - Displays today's field technician map positions
 * Derived from job status GPS near properties or configured offices
 */
function GPSTrackingMapView({ entries, loading, refetch }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef(new Map());
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [apiKey, setApiKey] = useState(null);
  const [mapContainerReady, setMapContainerReady] = useState(false);

  const { data: apiKeyData, loading: apiKeyLoading } = useQuery(GET_GOOGLE_MAPS_API_KEY_FOR_MAP);

  // Callback ref to know when the container is mounted
  const setMapRef = (node) => {
    mapRef.current = node;
    if (node) {
      setMapContainerReady(true);
    }
  };

  // Get API key from backend or environment
  useEffect(() => {
    if (apiKeyData?.getGoogleMapsApiKeyForMap) {
      setApiKey(apiKeyData.getGoogleMapsApiKeyForMap);
    } else if (process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
      setApiKey(process.env.REACT_APP_GOOGLE_MAPS_API_KEY);
    }
  }, [apiKeyData]);

  // Load Google Maps script when API key is available
  useEffect(() => {
    if (mapLoaded || !mapContainerReady || !mapRef.current) return;

    // Determine the API key to use (check both state and env)
    const keyToUse = apiKey || process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    
    // If still loading API key from backend and no env key, wait
    if (apiKeyLoading && !process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
      return;
    }

    // If no API key available after loading completes, show error (handled in render)
    if (!apiKeyLoading && !keyToUse) {
      return;
    }

    // If we have a key, proceed with loading
    if (!keyToUse) {
      return;
    }

    // Check if Google Maps is already loaded
    if (window.google && window.google.maps && mapRef.current) {
      if (!mapInstanceRef.current) {
        requestAnimationFrame(() => {
          initializeMap();
        });
      }
      return;
    }

    // Check if script tag already exists
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      // Script already exists, wait for it to load
      if (window.google && window.google.maps) {
        requestAnimationFrame(() => {
          initializeMap();
        });
      } else {
        const handleLoad = () => {
          if (mapRef.current && !mapInstanceRef.current) {
            requestAnimationFrame(() => {
              initializeMap();
            });
          }
        };
        
        // Check if script is already loaded
        if (existingScript.readyState === 'complete' || existingScript.readyState === 'loaded') {
          // Script loaded but Google Maps not available yet, wait a bit
          setTimeout(handleLoad, 100);
        } else {
          existingScript.addEventListener('load', handleLoad);
        }
      }
      return;
    }

    // Create and load script with API key
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${keyToUse}&libraries=geometry`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (mapRef.current && !mapInstanceRef.current) {
        requestAnimationFrame(() => {
          initializeMap();
        });
      }
    };
    script.onerror = () => {
      // Failed to load Google Maps script
    };

    document.head.appendChild(script);

    return () => {
      // Don't remove script on cleanup - keep it loaded for performance
    };
  }, [mapLoaded, apiKey, apiKeyLoading, mapContainerReady]);

  // Initialize map
  const initializeMap = () => {
    if (!window.google || !mapRef.current || mapInstanceRef.current) return;

    // If map container is hidden, wait for it to be visible
    const containerRect = mapRef.current.getBoundingClientRect();
    if (containerRect.width === 0 || containerRect.height === 0) {
      // Container is hidden, wait a bit and try again
      setTimeout(() => {
        if (mapRef.current && !mapInstanceRef.current) {
          initializeMap();
        }
      }, 100);
      return;
    }

    const defaultCenter = { lat: 32.7765, lng: -79.9311 }; // Charleston, SC area (adjust as needed)

    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: defaultCenter,
      zoom: 11,
      mapTypeId: window.google.maps.MapTypeId.ROADMAP,
      styles: [
        {
          featureType: 'poi',
          stylers: [{ visibility: 'off' }],
        },
        {
          featureType: 'poi.business',
          stylers: [{ visibility: 'off' }],
        },
      ],
      disableDefaultUI: false,
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: window.google.maps.ControlPosition.TOP_RIGHT,
        mapTypeIds: [
          window.google.maps.MapTypeId.ROADMAP,
          window.google.maps.MapTypeId.SATELLITE,
        ],
      },
    });

    setMapLoaded(true);

    setTimeout(() => {
      if (window.google && window.google.maps && mapInstanceRef.current) {
        window.google.maps.event.trigger(mapInstanceRef.current, 'resize');
      }
    }, 100);
  };

  // Update markers when entries change or map becomes visible
  useEffect(() => {
    // Wait for map to be loaded and ready
    if (!mapLoaded || !mapInstanceRef.current || !window.google?.maps) {
      return;
    }

    // Check if container is visible - markers won't render on hidden containers
    if (mapRef.current) {
      const containerRect = mapRef.current.getBoundingClientRect();
      if (containerRect.width === 0 || containerRect.height === 0) {
        // Container is hidden, wait a bit and try again
        const retryTimer = setTimeout(() => {
          if (mapRef.current) {
            const newRect = mapRef.current.getBoundingClientRect();
            if (newRect.width > 0 && newRect.height > 0) {
              // Container is now visible, trigger the effect again by checking entries
              // This will be handled by the dependency array
            }
          }
        }, 100);
        return () => clearTimeout(retryTimer);
      }

      // Container is visible, trigger resize to ensure map is properly sized
      setTimeout(() => {
        if (mapInstanceRef.current && window.google?.maps) {
          window.google.maps.event.trigger(mapInstanceRef.current, 'resize');
        }
      }, 50);
    }

    // If no entries, clear markers and return
    if (!entries || entries.length === 0) {
      markersRef.current.forEach((marker) => {
        marker.setMap(null);
      });
      markersRef.current.clear();
      return;
    }

    // Clear existing markers first
    markersRef.current.forEach((marker) => {
      marker.setMap(null);
    });
    markersRef.current.clear();

    // Create markers for each entry
    const bounds = new window.google.maps.LatLngBounds();
    let hasValidLocation = false;

    entries.forEach((entry) => {
      // New structure: CurrentGPSLocation has latitude, longitude directly
      if (!entry.latitude || !entry.longitude) {
        return; // Skip entries without GPS
      }

      const lat = parseFloat(entry.latitude);
      const lng = parseFloat(entry.longitude);
      const position = { lat, lng };
      bounds.extend(position);
      hasValidLocation = true;

      // Create marker with user's initial
      const userName = `${entry.user?.firstName || ''} ${entry.user?.lastName || ''}`.trim() || 'Unknown';
      const userInitial = userName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

      let fillColor = '#10B981';
      if (entry.locationType === 'OFFICE') {
        fillColor = '#6366F1';
      } else if (entry.locationType === 'PROPERTY') {
        fillColor = '#10B981';
      }

      // Create marker with custom icon
      const markerOptions = {
        position,
        map: mapInstanceRef.current,
        title: userName,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: fillColor,
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
        },
        label: {
          text: userInitial,
          color: '#FFFFFF',
          fontSize: '11px',
          fontWeight: 'bold',
        },
        optimized: false, // Disable optimization to ensure markers render
      };

      const marker = new window.google.maps.Marker(markerOptions);

      // Create info window
      const infoWindow = new window.google.maps.InfoWindow({
        content: createInfoWindowContent(entry),
      });

      // Add click listener
      marker.addListener('click', () => {
        // Close all other info windows
        markersRef.current.forEach((m) => {
          if (m.infoWindow) {
            m.infoWindow.close();
          }
        });
        infoWindow.open(mapInstanceRef.current, marker);
        setSelectedEntry(entry);
        marker.infoWindow = infoWindow;
      });

      marker.infoWindow = infoWindow;
      markersRef.current.set(entry.userId, marker);
    });

    // Fit bounds to show all markers - use a delay to ensure markers are rendered
    if (hasValidLocation && markersRef.current.size > 0) {
      // Trigger map resize first to ensure it's properly sized
      if (mapInstanceRef.current && window.google?.maps) {
        window.google.maps.event.trigger(mapInstanceRef.current, 'resize');
      }

      setTimeout(() => {
        if (!mapInstanceRef.current || !window.google?.maps) {
          return;
        }

        try {
          // Check if bounds is valid (has at least one point)
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();
          
          // If bounds are valid, fit to them
          if (ne && sw && (ne.lat() !== sw.lat() || ne.lng() !== sw.lng())) {
            mapInstanceRef.current.fitBounds(bounds, {
              padding: 50, // Add padding around markers
            });
            
            // Don't zoom in too much if only one marker
            if (markersRef.current.size === 1) {
              const listener = window.google.maps.event.addListener(
                mapInstanceRef.current,
                'bounds_changed',
                function () {
                  if (mapInstanceRef.current.getZoom() > 15) {
                    mapInstanceRef.current.setZoom(15);
                  }
                  window.google.maps.event.removeListener(listener);
                }
              );
            }
          } else {
            // Single point or invalid bounds - center on first marker
            const firstMarker = Array.from(markersRef.current.values())[0];
            if (firstMarker && firstMarker.getPosition()) {
              mapInstanceRef.current.setCenter(firstMarker.getPosition());
              mapInstanceRef.current.setZoom(15);
            }
          }
        } catch (error) {
          // Fallback: center on first entry
          const firstEntry = entries.find(e => 
          e.latitude && e.longitude
          );
          if (firstEntry) {
            const lat = parseFloat(firstEntry.latitude);
            const lng = parseFloat(firstEntry.longitude);
            mapInstanceRef.current.setCenter({ lat, lng });
            mapInstanceRef.current.setZoom(15);
          }
        }
      }, 300); // Delay to ensure markers are fully rendered
    }
  }, [entries, mapLoaded]);

  const getActivityStatusText = (entry) => {
    if (entry.locationType === 'OFFICE') {
      return 'In office';
    }
    switch (entry.activityType) {
      case 'WORK_ORDER_CHECK_IN':
        return 'Work order — checked in';
      case 'WORK_ORDER_CHECK_OUT':
        return 'Work order — checked out';
      case 'JOB_AT_PROPERTY':
        return 'Job — at property';
      default:
        return 'At property';
    }
  };

  const createInfoWindowContent = (entry) => {
    const userName = `${entry.user?.firstName || ''} ${entry.user?.lastName || ''}`.trim() || 'Unknown';
    const formattedTime = entry.updatedAt ? format(new Date(entry.updatedAt), 'MMM d, yyyy h:mm a') : 'N/A';
    const locationLabel = entry.label || 'Unknown';
    const isOffice = entry.locationType === 'OFFICE';
    const borderColor = isOffice ? '#6366F1' : '#10B981';
    const statusText = getActivityStatusText(entry);
    const statusBg = isOffice ? '#EEF2FF' : '#D1FAE5';
    const statusColor = isOffice ? '#3730A3' : '#065F46';
    const statusBorder = isOffice ? '#C7D2FE' : '#6EE7B7';

    return `
      <div style="min-width: 240px; padding: 12px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;">
        <h3 style="margin: 0 0 12px 0; font-weight: 600; font-size: 16px; color: #111827; border-bottom: 2px solid #E5E7EB; padding-bottom: 8px;">${userName}</h3>
        <div style="margin-bottom: 12px; padding: 10px; background: #F3F4F6; border-radius: 6px; border-left: 3px solid ${borderColor};">
          <div style="font-size: 14px; font-weight: 600; color: #111827;">${locationLabel}</div>
        </div>
        <div style="margin-bottom: 12px; font-size: 14px;">
          <strong style="color: #374151;">Last seen at:</strong>
          <span style="color: #6B7280;"> ${formattedTime}</span>
        </div>
        <div style="padding: 6px 12px; background: ${statusBg}; color: ${statusColor}; border-radius: 4px; font-size: 12px; font-weight: 600; display: inline-block; border: 1px solid ${statusBorder};">${statusText}</div>
      </div>
    `;
  };

  if (loading && !mapLoaded) {
    return (
      <div className="bg-white shadow rounded-lg p-8">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-gray-500">Loading map...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white overflow-hidden flex flex-col" style={{ height: 'calc(100vh - 4rem)' }}>
      <div className="px-4 py-2 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center justify-end gap-4 text-sm text-gray-600">
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-full bg-green-500" aria-hidden="true" />
            Property
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-full bg-indigo-500" aria-hidden="true" />
            In office
          </span>
          <span>
            {entries?.length || 0} on map
          </span>
        </div>
      </div>
      <div className="relative flex-1 w-full">
        <div ref={setMapRef} style={{ height: '100%', width: '100%' }} />
        {(!mapLoaded || apiKeyLoading) && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="text-center">
              <div className="text-gray-500 mb-2">Loading Google Maps...</div>
              {!apiKey && !apiKeyLoading && !process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
                <div className="text-sm text-yellow-600 max-w-md mx-auto px-4">
                  Google Maps API key not configured. Please configure it in{' '}
                  <a href="/dashboard/settings" className="text-blue-600 hover:underline">
                    Settings → GPS
                  </a>{' '}
                  or set REACT_APP_GOOGLE_MAPS_API_KEY environment variable.
                </div>
              )}
            </div>
          </div>
        )}
        {mapLoaded && (!entries || entries.length === 0) && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <div className="text-center text-gray-500">
              <svg
                className="mx-auto h-12 w-12 text-gray-400 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <p>No map locations for today</p>
              <p className="text-sm mt-2">
                Technicians appear when job or work order GPS is near a property or configured office (Settings → GPS).
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default GPSTrackingMapView;
